import { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import { authService } from '../../services/auth.service';
import { useAuthStore } from '../../store/auth.store';

export default function SettingsPage() {
  const { user, setUser } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    if (!user) return;
    setName(user.name || '');
    setPhone(user.phone || '');
    setAvatarUrl(user.avatarUrl || '');
  }, [user]);

  const handleSaveProfile = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      const profile = await authService.updateProfile({
        name,
        phone: phone || null,
        avatarUrl: avatarUrl || null,
      });
      setUser(profile);
      setSuccess('Perfil atualizado com sucesso');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao atualizar perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      setError('A confirmação da nova senha não confere');
      return;
    }
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      await authService.changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSuccess('Senha alterada com sucesso');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao alterar senha');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 3 }}>
        Minha Conta
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Stack spacing={3}>
        <Card>
          <CardHeader title="Perfil" subheader="Atualize seus dados e avatar" />
          <CardContent>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  src={avatarUrl || undefined}
                  sx={{ width: 72, height: 72, bgcolor: 'primary.main' }}
                >
                  {name?.charAt(0)?.toUpperCase()}
                </Avatar>
                <TextField
                  fullWidth
                  label="URL do avatar"
                  value={avatarUrl}
                  onChange={(e) => setAvatarUrl(e.target.value)}
                  placeholder="https://..."
                />
              </Stack>

              <TextField
                fullWidth
                label="Nome"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <TextField
                fullWidth
                label="Telefone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
              <Box>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSaveProfile}
                  disabled={loading || !name}
                >
                  Salvar Perfil
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardHeader title="Senha" subheader="Altere sua senha de acesso" />
          <Divider />
          <CardContent>
            <Stack spacing={2}>
              <TextField
                fullWidth
                type="password"
                label="Senha atual"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
              <TextField
                fullWidth
                type="password"
                label="Nova senha"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                helperText="Mínimo de 6 caracteres"
              />
              <TextField
                fullWidth
                type="password"
                label="Confirmar nova senha"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <Box>
                <Button
                  variant="contained"
                  onClick={handleChangePassword}
                  disabled={loading || currentPassword.length < 6 || newPassword.length < 6 || confirmPassword.length < 6}
                >
                  Atualizar Senha
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}
